import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Car, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated");
      nav("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Password update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="change-password-form">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Car size={22} className="text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-xl">MotoCraft</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Secure your account</div>
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 text-sm mb-6">
          <ShieldCheck size={16} />
          <span>You must change your password before continuing.</span>
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight">Set a new password</h1>
        <p className="text-sm text-muted-foreground mt-1">Minimum 8 characters. Use a unique password.</p>

        <div className="mt-8 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current Password</label>
            <input data-testid="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-card border border-border focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New Password</label>
            <input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8}
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-card border border-border focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirm New Password</label>
            <input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-card border border-border focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm" />
          </div>
          <button data-testid="cp-submit" type="submit" disabled={busy}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} Update password
          </button>
        </div>
      </form>
    </div>
  );
}
